<template>
  <div class="content-wrapper">
    <loading v-model:active="isLoading" :is-full-page="true" />

    <!-- Modals -->
    <SurveyModals v-model:show-measurement-modal="showMeasurementModal" v-model:show-modal="showModal"
      v-model:show-image-modal="showImageModal" v-model:show-tooth-selector-modal="showToothSelectorModal"
      v-model:show-r-cat-warning-modal="showRCatWarningModal" :information="information" :image-base64="imageBase64"
      :selected-tooth-number="selectedToothNumber" @measurement-confirmed="handleMeasurement"
      @tooth-selected="handleToothSelection" @rcat-confirmed="confirmRCatSelection"
      @rcat-cancelled="cancelRCatSelection" @high-risk-cancelled="cancelHighRiskSelection" />



    <!-- Question Sections -->
    <SurveyQuestionSections :questions="questions" :cross-system-questions="crossSystemQuestions"
      :pending-screening-system-type="fsm.state.pendingScreeningSystem" :is-r-cat-system="isRCatSystem"
      @select-response="selectResponse" @show-image="showImage" @show-information="showInformation"
      @show-measurement-tool="showMeasurementTool" @show-tooth-selector="showToothSelector"
      @screening-confirmed="handleScreeningConfirmed" @screening-cancelled="handleScreeningCancelled"
      @screening-error="handleScreeningError" @reset-cross-questions="resetCrossQuestions"
      :preselected-screening-ids="preselectedScreeningIds" />

    <!-- Action Buttons -->
    <SurveyActionButtons :is-loading="isLoading" :has-inline-screening="fsm.state.status === 'SCREENING_PENDING'"
      :pending-screening-system-type="fsm.state.pendingScreeningSystem" @back-clicked="back" @finish-clicked="finish" />
  </div>
</template>

<script>
import { onMounted, reactive, toRefs, nextTick, computed } from 'vue';
import { useRouter } from 'vue-router';
import axios from '../../services/axios-service';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

import SurveyModals from './components/survey/SurveyModals.vue';
import SurveyQuestionSections from './components/survey/SurveyQuestionSections.vue';
import SurveyActionButtons from './components/survey/SurveyActionButtons.vue';

import { createSurveyMachine } from './components/survey/utils/useSurveyMachine';
import { saveAllQuestions, loadAllQuestions } from './components/survey/utils/storage';

export default {
  name: 'Survey',
  components: {
    Loading,
    SurveyModals,
    SurveyQuestionSections,
    SurveyActionButtons
  },
  setup() {
    const router = useRouter();
    const state = reactive({
      isLoading: false,
      questions: [],
      crossSystemQuestions: [],
      selectedResponseIds: [],

      information: '',
      imageBase64: '',

      showModal: false,
      showImageModal: false,
      showMeasurementModal: false,
      showToothSelectorModal: false,
      showRCatWarningModal: false,

      currentQuestion: null,
      selectedToothNumber: '',
      pendingQuestion: null,
      pendingResponse: null,
    });

    const isRCatSystem = localStorage.getItem('@app-system') === 'rcat';
    const activeSystem = isRCatSystem ? 1 : 0;
    const fsm = createSurveyMachine({ activeSystem });

    onMounted(() => {
      const storedQuestionsJson = localStorage.getItem('@screening-questions');
      if (!storedQuestionsJson) {
        router.push('$1');
        return;
      }
      const screeningQuestions = JSON.parse(storedQuestionsJson);
      loadPrimaryQuestions(screeningQuestions);
    });

    function loadPrimaryQuestions(screeningQuestions) {
      state.isLoading = true;
      const dto = {
        selectedServiceQuestionIds: screeningQuestions.filter((s) => s.response === true).map((s) => s.id),
        deselectedServiceQuestionIds: screeningQuestions.filter((s) => s.response === false).map((s) => s.id),
      };
      axios
        .post(`/api/question/fetch?systemType=${activeSystem}`, dto)
        .then((resp) => {
          state.isLoading = false;
          state.questions = resp.data;
          rehydrateQuestions();
          evaluateTriggers();
        })
        .catch((err) => {
          state.isLoading = false;
          console.error('Error loading questions:', err);
          alert('Error loading questions. Please try again.');
        });
    }

    function evaluateTriggers() {
      // 1. Compute currently triggered systems from PRIMARY questions
      const triggered = new Set();
      for (const q of state.questions) {
        for (const r of q.responses) {
          if (r.isSelected && r.loadSystemType != null) {
            triggered.add(r.loadSystemType);
          }
        }
      }

      // Keep a copy BEFORE update so we can know what was removed
      const previouslyTriggered = new Set(fsm.state.triggeredSystems);

      // 2. Update FSM
      fsm.updateTriggers(triggered);

      // 3. Remove questions for systems no longer triggered
      const keepSystems = triggered;
      const removedSystems = [...previouslyTriggered].filter(s => !keepSystems.has(s));

      if (removedSystems.length) {
        // Purge cross questions & selected ids for those systems
        state.crossSystemQuestions = state.crossSystemQuestions.filter(q => {
          const keep = !removedSystems.includes(q.loadedFromSystemType);
          if (!keep) {
            q.responses.forEach(resp => {
              if (resp.isSelected) {
                state.selectedResponseIds = state.selectedResponseIds.filter(id => id !== resp.id);
              }
            });
          }
          return keep;
        });

        removedSystems.forEach(sys => {
          fsm.state.loadedSystems.delete(sys);
          fsm.state.screenedSystems.delete(sys);
        });
      }

      saveAllQuestions(state.questions, state.crossSystemQuestions);
      checkAndQueueScreening();
    }

    function applyCrossPreselect(targetQuestions) {
      const links = [];
      for (const q of state.questions) {
        if (q.isCrossSystemLoaded) continue;
        for (const r of q.responses) {
          if (r.isSelected && r.linkedToQuestionId && r.linkedToQuestionResponseId) {
            links.push({
              targetQuestionId: r.linkedToQuestionId,
              targetResponseId: r.linkedToQuestionResponseId,
            });
          }
        }
      }
      if (!links.length) return;

      for (const tq of targetQuestions) {
        const match = links.find(l => l.targetQuestionId === tq.id);
        if (!match) continue;

        if (!tq.multipleResponses) {
          tq.responses.forEach(rr => {
            if (rr.isSelected) {
              rr.isSelected = false;
              rr.autoSelected = false;
              state.selectedResponseIds = state.selectedResponseIds.filter(id => id !== rr.id);
            }
          });
        }

        const tr = tq.responses.find(rr => rr.id === match.targetResponseId);
        if (tr) {
          tr.isSelected = true;
          tr.autoSelected = true;
          if (!state.selectedResponseIds.includes(tr.id)) {
            state.selectedResponseIds.push(tr.id);
          }
        }
      }
    }

    const preselectedScreeningIds = computed(() => {
      const targetSystem = fsm.state.pendingScreeningSystem;
      if (targetSystem == null) return [];
      const ids = new Set();
      for (const q of state.questions) {
        for (const r of q.responses) {
          if (
            r.isSelected === true &&
            (r.linkedToScreeningQuestionId)
          ) {
            ids.add(r.linkedToScreeningQuestionId);
          }
        }
      }

      return Array.from(ids);
    });

    function handleScreeningConfirmed({ selectedServiceQuestionIds, deselectedServiceQuestionIds, systemType }) {
      fsm.confirmScreening(systemType);
      state.isLoading = true;
      const dto = {
        selectedServiceQuestionIds,
        deselectedServiceQuestionIds,
        selectedResponseIds: state.selectedResponseIds,
      };
      axios
        .post(`/api/question/fetch?systemType=${systemType}`, dto)
        .then((resp) => {
          state.crossSystemQuestions = state.crossSystemQuestions.filter((q) => q.loadedFromSystemType !== systemType);

          const deduped = resp.data.filter(
            (nq) => !state.questions.some((bq) => bq.id === nq.id) && !state.crossSystemQuestions.some((cq) => cq.id === nq.id)
          );

          // newQs.forEach((q) => {
          //   q.isCrossSystemLoaded = true;
          //   q.loadedFromSystemType = systemType;
          // });

          const newQs = deduped.filter(q => !hasAnyTriggerOption(q));
          newQs.forEach((q) => {
            q.isCrossSystemLoaded = true;
            q.loadedFromSystemType = systemType;
          });

          applyCrossPreselect(newQs);

          state.crossSystemQuestions.push(...newQs);
          state.crossSystemQuestions.sort((a, b) => a.position - b.position);

          saveAllQuestions(state.questions, state.crossSystemQuestions);

          fsm.finishLoadingSecondary(systemType);

          // Scroll to cross-system questions after DOM updates
          nextTick(() => {
            const crossSystemSection = document.querySelector('.additional-questions-section');
            if (crossSystemSection) {
              crossSystemSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          });
        })
        .catch((err) => {
          console.error('Error loading cross-system questions:', err);
          alert('Error loading additional questions. Please try again.');
          fsm.cancelScreening();
        })
        .finally(() => {
          state.isLoading = false;
        });
    }

    function handleScreeningCancelled() {
      if (state.pendingResponse) {
        state.pendingResponse.isSelected = false;
        state.selectedResponseIds = state.selectedResponseIds.filter((id) => id !== state.pendingResponse.id);
      }
      fsm.cancelScreening();
      saveAllQuestions(state.questions, state.crossSystemQuestions);
    }

    function handleScreeningError(msg) {
      console.error('Screening error:', msg);
      alert('Error loading screening questions: ' + msg);
      handleScreeningCancelled();
    }

    function selectResponse({ question, response }) {
      if (question.title === 'Root curvature' && !question.measurementImageBase64) return;
      if (question.title === 'What is the position of the tooth?' && !question.selectedToothNumber) return;

      if (!question.multipleResponses) {
        question.responses.forEach((r) => {
          if (r.id !== response.id && r.isSelected) {
            r.isSelected = false;
            r.autoSelected = false;
            state.selectedResponseIds = state.selectedResponseIds.filter((id) => id !== r.id);
          }
        });
      }

      const willSelect = !response.isSelected;
      response.isSelected = willSelect;
      response.autoSelected = false;
      if (willSelect) {
        if (!state.selectedResponseIds.includes(response.id)) {
          state.selectedResponseIds.push(response.id);
        }
      } else {
        state.selectedResponseIds = state.selectedResponseIds.filter((id) => id !== response.id);
      }

      const anyTriggers = state.questions.some(q =>
        q.responses.some(r => r.isSelected && r.loadSystemType != null)
      );
      if (!anyTriggers && fsm.state.status === 'SCREENING_PENDING') {
        fsm.cancelScreening();
        state.crossSystemQuestions = [];
        fsm.state.loadedSystems.clear();
        fsm.state.screenedSystems.clear();
        saveAllQuestions(state.questions, state.crossSystemQuestions);
      }

      if (willSelect && response.weight === 6 && isRCatSystem) {
        state.pendingQuestion = question;
        state.pendingResponse = response;
        state.showRCatWarningModal = true;
      } else {
        handleResponseSelection();
      }
    }

    function handleResponseSelection() {
      saveAllQuestions(state.questions, state.crossSystemQuestions);
      evaluateTriggers();
      checkAndQueueScreening();
    }

    function showMeasurementTool(question) {
      state.currentQuestion = question;
      state.showMeasurementModal = true;
    }

    function showToothSelector(question) {
      state.currentQuestion = question;
      state.selectedToothNumber = question.selectedToothNumber || '';
      state.showToothSelectorModal = true;
    }

    function showInformation(item) {
      state.information = item.information;
      state.showModal = true;
    }

    function showImage(imageBase64) {
      state.imageBase64 = imageBase64;
      state.showImageModal = true;
    }

    function handleToothSelection(toothNumber) {
      state.showToothSelectorModal = false;
      if (state.currentQuestion) {
        state.currentQuestion.selectedToothNumber = toothNumber;
        autoSelectResponseBasedOnTooth(state.currentQuestion, toothNumber);
        saveAllQuestions(state.questions, state.crossSystemQuestions);
      }
    }

    function handleMeasurement({ annotatedImageBase64, originalImageBase64, angle, direction }) {
      state.showMeasurementModal = false;
      if (state.currentQuestion) {
        state.currentQuestion.measurementAngle = angle;
        state.currentQuestion.measurementImageBase64 = annotatedImageBase64;
        state.currentQuestion.imageBase64 = originalImageBase64;
        state.currentQuestion.measurementDirection = direction;
        autoSelectResponseBasedOnAngle(state.currentQuestion, angle);
        saveAllQuestions(state.questions, state.crossSystemQuestions);
      }
    }

    function autoSelectResponseBasedOnTooth(question, toothNumber) {
      const sorted = question.responses.sort((a, b) => a.position - b.position);
      sorted.forEach((r) => {
        r.isSelected = false;
        r.autoSelected = false;
      });
      const anteriorPremolars = ['11', '12', '13', '14', '15', '21', '22', '23', '24', '25', '31', '32', '33', '34', '35', '41', '42', '43', '44', '45'];
      const firstSecondMolars = ['16', '17', '26', '27', '36', '37', '46', '47'];
      const thirdMolars = ['18', '28', '38', '48'];
      if (anteriorPremolars.includes(toothNumber) && sorted[0]) markSelected(sorted[0]);
      else if (firstSecondMolars.includes(toothNumber) && sorted[1]) markSelected(sorted[1]);
      else if (thirdMolars.includes(toothNumber) && sorted[2]) markSelected(sorted[2]);
      question.responses = sorted;
    }

    function autoSelectResponseBasedOnAngle(question, ang) {
      const angleValue = parseFloat(ang);
      const sorted = question.responses.sort((a, b) => a.position - b.position);
      sorted.forEach((r) => (r.isSelected = false));
      if (angleValue < 30 && sorted[0]) markSelected(sorted[0]);
      else if (angleValue >= 30 && angleValue < 45 && sorted[1]) markSelected(sorted[1]);
      else if (angleValue >= 45 && angleValue < 60 && sorted[2]) markSelected(sorted[2]);
      else if (angleValue >= 60 && sorted[3]) markSelected(sorted[3]);
      question.responses = sorted;
    }

    function markSelected(resp) {
      resp.isSelected = true;
      resp.autoSelected = true;
      if (!state.selectedResponseIds.includes(resp.id)) state.selectedResponseIds.push(resp.id);
    }

    function confirmRCatSelection() {
      state.showRCatWarningModal = false;
      handleResponseSelection();
      state.pendingQuestion = null;
      state.pendingResponse = null;
    }

    function cancelRCatSelection() {
      if (state.pendingResponse) {
        state.pendingResponse.isSelected = false;
        state.selectedResponseIds = state.selectedResponseIds.filter((id) => id !== state.pendingResponse.id);
      }
      state.showRCatWarningModal = false;
      saveAllQuestions(state.questions, state.crossSystemQuestions);
      state.pendingQuestion = null;
      state.pendingResponse = null;
      evaluateTriggers();
    }

    function cancelHighRiskSelection() {
      const all = [...state.questions, ...state.crossSystemQuestions];
      for (const q of all) {
        const hasSel = q.responses.some((r) => r.isSelected === true);
        if (!hasSel) {
          const sorted = q.responses.sort((a, b) => a.position - b.position);
          sorted.forEach((r) => {
            r.isSelected = false;
            r.autoSelected = false;
          });
          if (sorted.length > 0) markSelected(sorted[0]);
        }
      }
      state.showRCatWarningModal = false;
      saveAllQuestions(state.questions, state.crossSystemQuestions);
      state.pendingQuestion = null;
      state.pendingResponse = null;
      router.push("/summary");
    }

    function back() {
      router.push("/screening");
    }

    function finish() {
      const all = [...state.questions, ...state.crossSystemQuestions];
      for (const q of all) {
        q.requiresResponse = false;
        const cnt = q.responses.filter((r) => r.isSelected).length;
        if (cnt === 0) {
          q.requiresResponse = true;
          const el = document.getElementById(q.id);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
      router.push("/summary");
    }

    function rehydrateQuestions() {
      const stored = loadAllQuestions();
      state.selectedResponseIds = [];
      state.crossSystemQuestions = [];

      const baseMap = new Map(state.questions.map((q) => [q.id, q]));
      for (const sq of stored) {
        if (sq.isCrossSystemLoaded) {
          // state.crossSystemQuestions.push(sq);
          // sq.responses.forEach((r) => r.isSelected && state.selectedResponseIds.push(r.id));
          if (!hasAnyTriggerOption(sq)) {
            state.crossSystemQuestions.push(sq);
            sq.responses.forEach((r) => r.isSelected && state.selectedResponseIds.push(r.id));
          } else {
            // Drop any previously selected IDs from filtered-out cross questions
            sq.responses.forEach((r) => {
              if (r.isSelected) {
                state.selectedResponseIds = state.selectedResponseIds.filter(id => id !== r.id);
              }
            });
          }

        } else {
          const bq = baseMap.get(sq.id);
          if (bq) {
            Object.assign(bq, {
              measurementImageBase64: sq.measurementImageBase64,
              measurementAngle: sq.measurementAngle,
              measurementDirection: sq.measurementDirection,
              selectedToothNumber: sq.selectedToothNumber,
            });
            bq.responses.forEach((br) => {
              const sr = sq.responses.find((rr) => rr.id === br.id);
              if (sr) {
                br.isSelected = sr.isSelected;
                br.autoSelected = sr.autoSelected;
                if (br.isSelected) state.selectedResponseIds.push(br.id);
              }
            });
          }
        }
      }
      state.crossSystemQuestions.sort((a, b) => a.position - b.position);
      applyCrossPreselect(state.crossSystemQuestions);

      checkAndQueueScreening();
    }

    function resetCrossQuestions() {
      state.crossSystemQuestions = [];
      state.selectedResponseIds = state.selectedResponseIds.filter(id => {
        return state.questions.some(q =>
          q.responses.some(r => r.id === id)
        );
      });

      fsm.cancelScreening();

      fsm.state.loadedSystems.clear();
      fsm.state.screenedSystems.clear();

      saveAllQuestions(state.questions, state.crossSystemQuestions);

      const currentlyTriggered = new Set();
      for (const q of state.questions) {
        for (const r of q.responses) {
          if (r.isSelected && r.loadSystemType != null) {
            currentlyTriggered.add(r.loadSystemType);
          }
        }
      }

      const needsScreening = [...currentlyTriggered].filter((s) => !fsm.state.screenedSystems.has(s));

      if (needsScreening.length > 0) {
        fsm.state.pendingScreeningSystem = needsScreening[0];
        fsm.state.status = 'SCREENING_PENDING';
      }

      evaluateTriggers();
      checkAndQueueScreening();
    }


    function checkAndQueueScreening() {
      const norm = (v) => (v == null ? v : Number(v));

      const triggered = new Set();
      for (const q of state.questions) {
        for (const r of q.responses) {
          if (r.isSelected && r.loadSystemType != null) {
            triggered.add(norm(r.loadSystemType));
          }
        }
      }

      fsm.updateTriggers(triggered);

      fsm.state.loadedSystems.clear();
      const loadedCountsBySystem = new Map();
      for (const q of state.crossSystemQuestions) {
        if (q.loadedFromSystemType != null) {
          const s = norm(q.loadedFromSystemType);
          fsm.state.loadedSystems.add(s);
          loadedCountsBySystem.set(s, (loadedCountsBySystem.get(s) || 0) + 1);
        }
      }

      for (const s of Array.from(fsm.state.screenedSystems)) {
        const sNorm = norm(s);
        const isStillTriggered = triggered.has(sNorm);
        const hasLoadedQs = (loadedCountsBySystem.get(sNorm) || 0) > 0;
        if (!isStillTriggered || !hasLoadedQs) {
          fsm.state.screenedSystems.delete(s);
        }
      }

      const needsScreening = Array.from(triggered).filter(
        (s) => !fsm.state.loadedSystems.has(s) && !fsm.state.screenedSystems.has(s)
      );

      if (needsScreening.length > 0) {
        fsm.state.pendingScreeningSystem = needsScreening[0];
        fsm.state.status = 'SCREENING_PENDING';
      } else {
        fsm.state.pendingScreeningSystem = null;
        if (fsm.state.status === 'SCREENING_PENDING') {
          fsm.state.status = 'IDLE';
        }
      }
    }

    function hasAnyTriggerOption(q) {
      return Array.isArray(q?.responses) && q.responses.some(r => r.loadSystemType != null);
    }


    return {
      ...toRefs(state),
      fsm,
      isRCatSystem,

      selectResponse,
      showMeasurementTool,
      showToothSelector,
      showInformation,
      showImage,
      handleToothSelection,
      handleMeasurement,
      confirmRCatSelection,
      cancelRCatSelection,
      cancelHighRiskSelection,

      handleScreeningConfirmed,
      handleScreeningCancelled,
      handleScreeningError,
      resetCrossQuestions,
      preselectedScreeningIds,

      back,
      finish,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../../breakpoints.scss';

.content-wrapper {
  margin: 0 auto;
  padding: 0px 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
</style>